// src/utils/promptBuilder.js

export function getEffectivePrompt(item) {
  if (!item) return null;
  return item.updated_prompt || item.starter_prompt || null;
}

export function buildToneShiftPrompt({
  inputText,
  messages,
  voices, roles, formats, audiences, modifiers,
  selectedVoice, selectedRole, selectedFormat, selectedAudiences,
  modifierLevelsMap,           // pruned: result of activeModifierLevels(...)
  selectedModelInfo,           // { display, model }
  getModifierLevelName,        // (modifierName, level) -> level_name
  getLevelDisplayName          // (modifier, level) -> display string
}) {
  const json = { 
    task: 'Your task is to rewrite provided text using the following instructions. IMPORTANT: You must respond with a valid JSON object containing two fields: "additional_context" (any explanatory text about the transformation) and "response" (the rewritten text). Format your response as: {"additional_context": "...", "response": "..."}'
  };

  // Original message
  if (inputText?.trim()) {
    json.original_message = inputText.trim();
  } else {
    const firstUser = messages.find(m => m.type === 'input');
    json.original_message = firstUser ? firstUser.text : '(No text entered)';
  }

  // Voice
  if (selectedVoice) {
    const voice = voices.find(v => v.id === selectedVoice);
    const p = getEffectivePrompt(voice);
    if (p) json.voice = p;
  }

  // Role
  if (selectedRole) {
    const role = roles.find(r => r.id === selectedRole);
    const p = getEffectivePrompt(role);
    if (p) json.role = p;
  }

  // Format
  if (selectedFormat) {
    const format = formats.find(f => f.id === selectedFormat);
    const p = getEffectivePrompt(format);
    if (p) json.format = p;
  }

  // Audience (join prompts with AND)
  if (selectedAudiences?.length) {
    const prompts = selectedAudiences
      .map(id => {
        const a = audiences.find(x => x.id === id);
        return getEffectivePrompt(a);
      })
      .filter(Boolean);
    if (prompts.length) json.audience = prompts.join(' AND ');
  }

  // Modifiers (join with AND)
  const active = Object.entries(modifierLevelsMap).map(([modifierId, level]) => {
    const modifier = modifiers.find(m => m.id === modifierId);
    if (!modifier) return null;

    const levelName = getModifierLevelName(modifier.name, level);
    const levelObj = modifier.levels?.find(l => l.level_name === levelName);
    const p = levelObj ? getEffectivePrompt(levelObj) : null;
    const display = getLevelDisplayName(modifier, level);
    return p ? `${display}: ${p}` : null;
  }).filter(Boolean);

  if (active.length) json.modifiers = active.join(' AND ');
  if (selectedModelInfo) json.model = `${selectedModelInfo.display} (${selectedModelInfo.model})`;

  // Build text prompt (include convo history for parameter-only changes)
  const parts = [json.task];
  if (json.voice)     parts.push(`Voice: ${json.voice}`);
  if (json.role)      parts.push(`Role: ${json.role}`);
  if (json.format)    parts.push(`Format: ${json.format}`);
  if (json.audience)  parts.push(`Audience: ${json.audience}`);
  if (json.modifiers) parts.push(`Modifiers: ${json.modifiers}`);

  const hasNewInput = inputText?.trim()?.length > 0;
  if (!hasNewInput && messages?.length) {
    const history = [];
    const firstUser = messages.find(m => m.type === 'input');
    if (firstUser) history.push(`User: ${firstUser.text}`);
    const aiMsgs = messages.filter(m => m.type === 'output').slice(-2);
    aiMsgs.forEach(m => history.push(`Assistant: ${m.text}`));
    if (history.length) parts.push(`Conversation history:\n${history.join('\n')}`);
  }

  parts.push(`original message:\n${json.original_message}`);
  if (!hasNewInput && messages?.length) {
    parts.push(`Continue iterating on this message with the updated parameters.`);
  }
  if (selectedModelInfo) {
    parts.push(`Model: ${selectedModelInfo.display} (${selectedModelInfo.model})`);
  }
  
  // Add JSON format requirement
  parts.push(`\nIMPORTANT: Respond with a valid JSON object containing exactly two fields:
- "additional_context": Any explanatory text about the transformation (can be empty string if no additional context)
- "response": The rewritten text

Your response must be valid JSON in this format: {"additional_context": "...", "response": "..."}`);

  return { json, text: parts.join('\n\n') };
}
